import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { getSignerContractByAddress } from '../contracts/contract';
import { useNetwork } from '../contexts/NetworkContext';
import { useEscrow } from '../contexts/EscrowContext';
import './ArbiterSettings.css';

interface ArbiterSettingsProps {
  signer: ethers.Signer;
  onArbiterChanged?: () => void;
}

const ArbiterSettings: React.FC<ArbiterSettingsProps> = ({ signer, onArbiterChanged }) => {
  const { currentNetwork } = useNetwork();
  const { contractAddress } = useEscrow();
  const [currentArbiter, setCurrentArbiter] = useState<string>('');
  const [newArbiter, setNewArbiter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const loadCurrentArbiter = useCallback(async () => {
    if (!currentNetwork || !contractAddress) return;
    
    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const arbiter = await contract.arbiter();
      setCurrentArbiter(arbiter);
    } catch (err) {
      console.error('Failed to load current arbiter:', err);
      setError('Failed to load current arbiter');
    }
  }, [signer, currentNetwork, contractAddress]);

  useEffect(() => {
    loadCurrentArbiter();
  }, [loadCurrentArbiter]);

  const handleSetArbiter = async () => {
    if (!newArbiter.trim()) {
      setError('Please enter a valid arbiter address');
      return;
    }

    if (!ethers.isAddress(newArbiter)) {
      setError('Please enter a valid Ethereum address');
      return;
    }

    if (newArbiter.toLowerCase() === currentArbiter.toLowerCase()) {
      setError('New arbiter address must be different from current arbiter');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!currentNetwork || !contractAddress) {
        throw new Error('No network selected or contract address not set');
      }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.setArbiter(newArbiter);
      
      setSuccess('Transaction submitted! Waiting for confirmation...');
      
      await tx.wait();
      
      setSuccess('Arbiter successfully changed!');
      setNewArbiter('');
      await loadCurrentArbiter();
      
      if (onArbiterChanged) {
        onArbiterChanged();
      }
    } catch (err: any) {
      console.error('Failed to set arbiter:', err);
      if (err.message?.includes('Not authorized')) {
        setError('Only the current arbiter can change the arbiter address');
      } else if (err.message?.includes('Invalid arbiter address')) {
        setError('Invalid arbiter address provided');
      } else if (err.message?.includes('Same arbiter address')) {
        setError('New arbiter address must be different from current address');
      } else {
        setError('Failed to set arbiter: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="arbiter-settings">
      <h3>Arbiter Settings</h3>
      
      <div className="current-arbiter">
        <label>Current Arbiter:</label>
        <div className="arbiter-address">
          {currentArbiter ? formatAddress(currentArbiter) : 'Loading...'}
        </div>
      </div>

      <div className="new-arbiter">
        <label htmlFor="newArbiter">New Arbiter Address:</label>
        <input
          id="newArbiter"
          type="text"
          value={newArbiter}
          onChange={(e) => setNewArbiter(e.target.value)}
          placeholder="0x..."
          disabled={loading}
        />
        <button
          onClick={handleSetArbiter}
          disabled={loading || !newArbiter.trim()}
          className="set-arbiter-btn"
        >
          {loading ? 'Setting...' : 'Set New Arbiter'}
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}
    </div>
  );
};

export default ArbiterSettings;
